import { InputFile } from '@snowball-tech/fractal/dist/src/components/InputFile'
import { TabContent as FractalTabContent } from '@snowball-tech/fractal/dist/src/components/Tabs'

import { useCallback, useRef, useState } from 'react'

import constant from 'lodash/fp/constant'
import isEmpty from 'lodash/fp/isEmpty'
import isFunction from 'lodash/fp/isFunction'
import isString from 'lodash/fp/isString'

import type {
  HTTPError,
  MediaProviderTabContentProps,
  Upload,
} from '../../../types'

import { MAX_DIRECT_UPLOAD_SIZE } from '../../../constants'
import { AllowedMediaSources } from '../../../enums'
import { handleFiles } from '../../../helpers/files'
import useTranslations from '../../../hooks/useTranslations'
import DisplayError from '../../DisplayError'

const provider = AllowedMediaSources.Upload

export default function Upload({
  handleFile,
  onPick,
  setIsLoading,
  setProgress,
  type,
}: MediaProviderTabContentProps) {
  const $t = useTranslations()

  const inputFileRef = useRef<{
    fileInput: HTMLInputElement
    trigger: HTMLButtonElement
  }>(null)

  const [error, setError] = useState('')
  const displayError = useCallback(
    (message: string, duration = 5000) => {
      setError(message)

      if (duration > 0) {
        setTimeout(() => {
          setError('')
        }, duration)
      }
    },
    [setError],
  )

  const resetFileInput = () => {
    if (inputFileRef.current !== null && inputFileRef.current.fileInput) {
      inputFileRef.current.fileInput.value = ''
    }
  }

  const handleFileUpload = useCallback(
    async (files: FileList | null) => {
      if (!files || !isFunction(handleFile)) {
        return
      }

      setIsLoading?.(true)

      await handleFiles(
        handleFile,
        [...files].filter(
          ({ type: fileType }) => type === 'file' || fileType.startsWith(type),
        ),
        AllowedMediaSources.Upload,
        {
          onError: (
            reason: string,
            {
              error: fileError,
              file,
              max,
            }: { error: string | Error; file: File; max?: number },
          ) => {
            switch (reason) {
              case 'empty_file': {
                displayError($t(`editor.${type}.loader.paste.error.file_empty`))

                break
              }
              case 'too_big': {
                displayError(
                  $t(`editor.${type}.loader.upload.error.file_too_big`, {
                    maxSize: (max ?? MAX_DIRECT_UPLOAD_SIZE) / 1024 / 1024,
                    size: Math.ceil(file.size / 1024 / 1024),
                  }),
                )

                break
              }
              case 'upload_error': {
                displayError($t.http(fileError as HTTPError))

                break
              }
              default: {
                if (!reason.includes('abort')) {
                  displayError(
                    isString(fileError) ? fileError : fileError.toString(),
                  )
                }
              }
            }
          },
          onProcessingEnd: () => {
            resetFileInput()
          },
          onProcessingSuccess: ({
            image,
            url,
          }: {
            url: string
            image?: HTMLImageElement
          }) => {
            onPick(
              url,
              provider,
              type === 'image' && image
                ? {
                    height: image.naturalHeight,
                    width: image.naturalWidth,
                  }
                : undefined,
            )
          },
          onProgress: (progress) => setProgress?.(progress),
        },
      )

      setIsLoading?.(constant(false))
      setProgress?.(null)
    },
    [$t, displayError, handleFile, onPick, setIsLoading, setProgress, type],
  )

  return (
    <FractalTabContent name="upload">
      <div className="flex flex-col gap-2">
        <InputFile
          ref={inputFileRef}
          accept={`${type}/*`}
          label={$t(`editor.${type}.loader.${provider}.button`)}
          triggerProps={{ fullWidth: true }}
          onChange={handleFileUpload}
        />

        {!isEmpty(error) && <DisplayError display message={error} />}
      </div>
    </FractalTabContent>
  )
}
